import type { CTRL_SCHEMA } from "@repo/sdk/types";
import { useHttp, type HttpConfiguration } from "@repo/utils/hooks";
import { useCtrlClient } from "../contexts/CtrlApiProvider";

type WhoamiResponse = CTRL_SCHEMA["OpenapigenWhoamiResponse"];

/**
 * Hook for fetching current authenticated user information using SWR
 *
 * @param config - Optional SWR configuration
 * @returns SWR response with user data, error, loading states, and mutation function
 *
 * @example
 * ```tsx
 * function CurrentUser() {
 *   const { data: user, error, isLoading } = useWhoAmI()
 *
 *   if (isLoading) return <div>Loading...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *
 *   return (
 *     <div>
 *       <h3>Current User</h3>
 *       <p>ID: {user?.user_id}</p>
 *       <p>Email: {user?.email}</p>
 *       <p>Username: {user?.preferred_username}</p>
 *     </div>
 *   )
 * }
 * ```
 */
export function useWhoAmI(config?: HttpConfiguration) {
  const client = useCtrlClient();

  return useHttp<WhoamiResponse>(
    "whoami",
    async () => {
      return await client.whoami();
    },
    config
  );
}
